import {
  codeTemplates,
  exampleValues,
  formattedExampleValues,
  pythonExampleValues,
  phpExampleValues,
  csharpExampleValues,
  jsonExampleValues,
  httpMethodsWithBody,
  csharpHttpMethods
} from './constants/codeGeneration.js'

export const generateCodeSnippet = (api, endpoint, language) => {
    const baseUrl = api.baseUrl || 'https://api.lwsa.com'
    const path = endpoint.path
    const method = endpoint.method.toUpperCase()
    
    const templates = {
      javascript: generateJavaScript,
      python: generatePython,
      java: generateJava,
      csharp: generateCSharp,
      php: generatePHP,
      curl: generateCurl
    }
  
    return templates[language]?.(baseUrl, path, method, endpoint) || 'Linguagem não suportada'
  }
  
  const generateJavaScript = (baseUrl, path, method, endpoint) => {
    const hasBody = httpMethodsWithBody.includes(method)
    const params = endpoint.parameters || []
    const bodyParams = params.filter(p => p.in === 'body' || !p.in)
    const queryParams = params.filter(p => p.in === 'query')
    
    let code = `// ${endpoint.description || `Requisição ${method} para ${path}`}\n`
    code += `// Base URL: ${baseUrl}\n\n`
    
    if (queryParams.length > 0) {
      code += `// Parâmetros de consulta\n`
      code += `const queryParams = new URLSearchParams({\n`
      queryParams.forEach(param => {
        code += `  ${param.name}: '${getExampleValue(param.type)}', // ${param.type}${param.required ? ' (obrigatório)' : ' (opcional)'}\n`
      })
      code += `});\n\n`
    }
    
    code += `const response = await fetch('${baseUrl}${path}'${queryParams.length > 0 ? ' + \'?\' + queryParams' : ''}, {\n`
    code += `  method: '${method}',\n`
    code += `  headers: {\n`
    code += `    'Content-Type': 'application/json',\n`
    code += `    'Authorization': 'Bearer YOUR_ACCESS_TOKEN'\n`
    code += `  }`
    
    if (hasBody && bodyParams.length > 0) {
      code += `,\n  body: JSON.stringify({\n`
      bodyParams.forEach(param => {
        code += `    ${param.name}: ${getExampleValueFormatted(param.type)}, // ${param.type}${param.required ? ' (obrigatório)' : ' (opcional)'}\n`
      })
      code += `  })`
    }
    
    code += `\n});\n\n`
    code += `if (response.ok) {\n`
    code += `  const data = await response.json();\n`
    code += `  console.log('Sucesso:', data);\n`
    code += `} else {\n`
    code += `  console.error('Erro:', response.status, await response.text());\n`
    code += `}`
    
    return code
  }
  
  const generatePython = (baseUrl, path, method, endpoint) => {
    const hasBody = httpMethodsWithBody.includes(method)
    const params = endpoint.parameters || []
    const bodyParams = params.filter(p => p.in === 'body' || !p.in)
    const queryParams = params.filter(p => p.in === 'query')
    
    let code = `# ${endpoint.description || `Requisição ${method} para ${path}`}\n`
    code += `# Base URL: ${baseUrl}\n\n`
    code += `import requests\nimport json\n\n`
    
    code += `url = "${baseUrl}${path}"\n`
    code += `headers = {\n`
    code += `    "Content-Type": "application/json",\n`
    code += `    "Authorization": "Bearer YOUR_ACCESS_TOKEN"\n`
    code += `}\n\n`
    
    if (queryParams.length > 0) {
      code += `# Parâmetros de consulta\n`
      code += `params = {\n`
      queryParams.forEach(param => {
        code += `    "${param.name}": ${getPythonExampleValue(param.type)},  # ${param.type}${param.required ? ' (obrigatório)' : ' (opcional)'}\n`
      })
      code += `}\n\n`
    }
    
    if (hasBody && bodyParams.length > 0) {
      code += `# Dados do corpo da requisição\n`
      code += `data = {\n`
      bodyParams.forEach(param => {
        code += `    "${param.name}": ${getPythonExampleValue(param.type)},  # ${param.type}${param.required ? ' (obrigatório)' : ' (opcional)'}\n`
      })
      code += `}\n\n`
    }
    
    const requestParams = []
    requestParams.push('url')
    requestParams.push('headers=headers')
    if (queryParams.length > 0) requestParams.push('params=params')
    if (hasBody && bodyParams.length > 0) requestParams.push('json=data')
    
    code += `response = requests.${method.toLowerCase()}(${requestParams.join(', ')})\n\n`
    code += `if response.status_code == 200:\n`
    code += `    result = response.json()\n`
    code += `    print("Sucesso:", result)\n`
    code += `else:\n`
    code += `    print(f"Erro {response.status_code}: {response.text}")`
    
    return code
  }
  
  const generatePHP = (baseUrl, path, method, endpoint) => {
    const hasBody = httpMethodsWithBody.includes(method)
    const params = endpoint.parameters || []
    const bodyParams = params.filter(p => p.in === 'body' || !p.in)
    
    let code = `<?php\n`
    code += `// ${endpoint.description || `Requisição ${method} para ${path}`}\n`
    code += `// Base URL: ${baseUrl}\n\n`
    
    code += `$url = "${baseUrl}${path}";\n`
    code += `$headers = [\n`
    code += `    "Content-Type: application/json",\n`
    code += `    "Authorization: Bearer YOUR_ACCESS_TOKEN"\n`
    code += `];\n\n`
    
    if (hasBody && bodyParams.length > 0) {
      code += `$data = [\n`
      bodyParams.forEach(param => {
        code += `    "${param.name}" => ${getPHPExampleValue(param.type)}, // ${param.type}${param.required ? ' (obrigatório)' : ' (opcional)'}\n`
      })
      code += `];\n\n`
    }
    
    code += `$ch = curl_init();\n`
    code += `curl_setopt($ch, CURLOPT_URL, $url);\n`
    code += `curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);\n`
    code += `curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);\n`
    code += `curl_setopt($ch, CURLOPT_CUSTOMREQUEST, "${method}");\n`
    
    if (hasBody && bodyParams.length > 0) {
      code += `curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));\n`
    }
    
    code += `\n$response = curl_exec($ch);\n`
    code += `$httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);\n`
    code += `curl_close($ch);\n\n`
    code += `if ($httpCode === 200) {\n`
    code += `    $result = json_decode($response, true);\n`
    code += `    echo "Sucesso: " . print_r($result, true);\n`
    code += `} else {\n`
    code += `    echo "Erro $httpCode: $response";\n`
    code += `}`
    
    return code
  }
  
  const generateCurl = (baseUrl, path, method, endpoint) => {
    const hasBody = httpMethodsWithBody.includes(method)
    const params = endpoint.parameters || []
    const bodyParams = params.filter(p => p.in === 'body' || !p.in)
    
    let code = `# ${endpoint.description || `Requisição ${method} para ${path}`}\n`
    code += `# Base URL: ${baseUrl}\n\n`
    
    code += `curl -X ${method} "${baseUrl}${path}" \\\n`
    code += `  -H "Content-Type: application/json" \\\n`
    code += `  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`
    
    if (hasBody && bodyParams.length > 0) {
      code += ` \\\n  -d '{\n`
      bodyParams.forEach((param, index) => {
        const comma = index < bodyParams.length - 1 ? ',' : ''
        code += `    "${param.name}": ${getJSONExampleValue(param.type)}${comma}  // ${param.type}${param.required ? ' (obrigatório)' : ' (opcional)'}\n`
      })
      code += `  }'`
    }
    
    return code
  }
  
  const generateJava = (baseUrl, path, method, endpoint) => {
    const hasBody = httpMethodsWithBody.includes(method)
    const params = endpoint.parameters || []
    const bodyParams = params.filter(p => p.in === 'body' || !p.in)
    
    let code = `// ${endpoint.description || `Requisição ${method} para ${path}`}\n`
    code += `// Base URL: ${baseUrl}\n\n`
    code += `import java.net.http.*;\n`
    code += `import java.net.URI;\n`
    code += `import java.time.Duration;\n\n`
    
    code += `HttpClient client = HttpClient.newBuilder()\n`
    code += `    .connectTimeout(Duration.ofSeconds(10))\n`
    code += `    .build();\n\n`
    
    if (hasBody && bodyParams.length > 0) {
      code += `String jsonBody = "{\\":\\"\\"}".replace("{\\":\\"\\"}",\n`
      code += `    "{\\"" + \n`
      bodyParams.forEach((param, index) => {
        const comma = index < bodyParams.length - 1 ? ' + "," + ' : ''
        code += `    "${param.name}\\":\\"${getExampleValue(param.type)}\\"${comma}\n`
      })
      code += `    + "}");\n\n`
    }
    
    code += `HttpRequest request = HttpRequest.newBuilder()\n`
    code += `    .uri(URI.create("${baseUrl}${path}"))\n`
    code += `    .header("Content-Type", "application/json")\n`
    code += `    .header("Authorization", "Bearer YOUR_ACCESS_TOKEN")\n`
    code += `    .${method}(${hasBody && bodyParams.length > 0 ? 'HttpRequest.BodyPublishers.ofString(jsonBody)' : 'HttpRequest.BodyPublishers.noBody()'})\n`
    code += `    .build();\n\n`
    
    code += `HttpResponse<String> response = client.send(request,\n`
    code += `    HttpResponse.BodyHandlers.ofString());\n\n`
    code += `if (response.statusCode() == 200) {\n`
    code += `    System.out.println("Sucesso: " + response.body());\n`
    code += `} else {\n`
    code += `    System.out.println("Erro " + response.statusCode() + ": " + response.body());\n`
    code += `}`
    
    return code
  }
  
  const generateCSharp = (baseUrl, path, method, endpoint) => {
    const hasBody = httpMethodsWithBody.includes(method)
    const params = endpoint.parameters || []
    const bodyParams = params.filter(p => p.in === 'body' || !p.in)
    
    let code = `// ${endpoint.description || `Requisição ${method} para ${path}`}\n`
    code += `// Base URL: ${baseUrl}\n\n`
    code += `using System;\n`
    code += `using System.Net.Http;\n`
    code += `using System.Text;\n`
    code += `using System.Threading.Tasks;\n`
    code += `using Newtonsoft.Json;\n\n`
    
    code += `var client = new HttpClient();\n`
    code += `client.DefaultRequestHeaders.Add("Authorization", "Bearer YOUR_ACCESS_TOKEN");\n\n`
    
    if (hasBody && bodyParams.length > 0) {
      code += `var data = new {\n`
      bodyParams.forEach(param => {
        code += `    ${param.name} = ${getCSharpExampleValue(param.type)}, // ${param.type}${param.required ? ' (obrigatório)' : ' (opcional)'}\n`
      })
      code += `};\n\n`
      code += `var json = JsonConvert.SerializeObject(data);\n`
      code += `var content = new StringContent(json, Encoding.UTF8, "application/json");\n\n`
    }
    
    const methodCall = csharpHttpMethods[method] || csharpHttpMethods.default
    
    if (hasBody && bodyParams.length > 0 && method !== 'DELETE') {
      code += `var response = await client.${methodCall}("${baseUrl}${path}", content);\n\n`
    } else {
      code += `var response = await client.${methodCall}("${baseUrl}${path}");\n\n`
    }
    
    code += `if (response.IsSuccessStatusCode) {\n`
    code += `    var result = await response.Content.ReadAsStringAsync();\n`
    code += `    Console.WriteLine("Sucesso: " + result);\n`
    code += `} else {\n`
    code += `    Console.WriteLine($"Erro {response.StatusCode}: {await response.Content.ReadAsStringAsync()}");\n`
    code += `}`
    
    return code
  }
  
  // Funções auxiliares para gerar valores de exemplo
  const getExampleValue = (type) => {
    return exampleValues[type?.toLowerCase()] || exampleValues.default
  }
  
  const getExampleValueFormatted = (type) => {
    return formattedExampleValues[type?.toLowerCase()] || formattedExampleValues.default
  }
  
  const getPythonExampleValue = (type) => {
    return pythonExampleValues[type?.toLowerCase()] || pythonExampleValues.default
  }
  
  const getPHPExampleValue = (type) => {
    return phpExampleValues[type?.toLowerCase()] || phpExampleValues.default
  }
  
  const getCSharpExampleValue = (type) => {
    return csharpExampleValues[type?.toLowerCase()] || csharpExampleValues.default
  }
  
  const getJSONExampleValue = (type) => {
    return jsonExampleValues[type?.toLowerCase()] || jsonExampleValues.default
  }